import { getReportMetricKind, getReportTotalColors, getReportTableSx } from '../../views/shared/report-table-styles';
import { getReportSurfaceSx } from '../../views/shared/report-design';
import { useMemo, useContext, useRef, useId, useCallback } from 'react';
import { Box, Paper, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { MaterialReactTable } from 'material-react-table';
import { useTheme, ThemeProvider } from '@mui/material';
import { AppVarsContext } from '../../context/AppVarsContext';

const fontSize = '12px';

const getNumericValue = (row, keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value !== undefined && value !== null && value !== '') {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) return parsed;
        }
    }
    return 0;
};

const getAwarenessMetrics = (row) => {
    const impressions = getNumericValue(row, ['IMPRESSIONS', 'impressions']);
    const clicks = getNumericValue(row, ['CLICKS', 'clicks']);
    const conversions = getNumericValue(row, ['CONVERSIONS', 'conversions']);
    const ctr = getNumericValue(row, ['CTR', 'ctr']) || (impressions > 0 ? (clicks / impressions) * 100 : 0);
    const convRate =
        getNumericValue(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']) ||
        (clicks > 0 ? (conversions / clicks) * 100 : 0);

    return {
        impressions,
        clicks,
        ctr,
        conversions,
        convRate
    };
};

const ViMarcTableCard = (props) => {
    const uidRef = useRef(useId());
    const instanceKey = useMemo(() => (props.instanceKey || props.title || uidRef.current), [props.instanceKey, props.title]);
    const compact = Boolean(props.compact);
    const { appVars } = useContext(AppVarsContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const dataArray = useMemo(() => (Array.isArray(props.data) ? props.data : []), [props.data]);
    const parsedTooltips = useMemo(() => {
        if (!appVars?.tooltips) return {};
        try {
            return JSON.parse(appVars.tooltips);
        } catch (error) {
            return {};
        }
    }, [appVars?.tooltips]);
    const getTip = useCallback((field) => parsedTooltips?.[field] || '', [parsedTooltips]);

    const totals = useMemo(() => {
        return dataArray.reduce((acc, row) => {
            const metrics = getAwarenessMetrics(row);
            acc.impressions += metrics.impressions;
            acc.clicks += metrics.clicks;
            acc.conversions += metrics.conversions;
            return acc;
        }, {
            impressions: 0,
            clicks: 0,
            conversions: 0
        });
    }, [dataArray]);

    const metricTotals = useMemo(() => ({
        ...totals,
        ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
        convRate: totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0
    }), [totals]);

    const surfacePaperSx = getReportSurfaceSx(theme);

    const awarenessColumns = useMemo(() => ([
        {
            header: (
                <Tooltip title={<div><Typography fontWeight={900}>Impressions</Typography><Typography>{getTip('impression')}</Typography></div>}>
                    <span>Impressions</span>
                </Tooltip>
            ),
            accessorKey: 'IMPRESSIONS',
            Cell: ({ row }) => <NumericFormat decimalScale={0} thousandSeparator="," value={getAwarenessMetrics(row.original).impressions} displayType="text" />,
            Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={metricTotals.impressions} displayType="text" />
        },
        {
            header: (
                <Tooltip title={<div><Typography fontWeight={900}>Clicks</Typography><Typography>{getTip('click')}</Typography></div>}>
                    <span>Clicks</span>
                </Tooltip>
            ),
            accessorKey: 'CLICKS',
            Cell: ({ row }) => <NumericFormat decimalScale={0} thousandSeparator="," value={getAwarenessMetrics(row.original).clicks} displayType="text" />,
            Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={metricTotals.clicks} displayType="text" />
        },
        {
            header: (
                <Tooltip title={<div><Typography fontWeight={900}>CTR</Typography><Typography>{getTip('ctr')}</Typography></div>}>
                    <span>CTR</span>
                </Tooltip>
            ),
            accessorKey: 'CTR',
            Cell: ({ row }) => <NumericFormat decimalScale={2} value={getAwarenessMetrics(row.original).ctr} displayType="text" suffix="%" />,
            Footer: () => <NumericFormat decimalScale={2} value={metricTotals.ctr} displayType="text" suffix="%" />
        },
        {
            header: (
                <Tooltip title={<div><Typography fontWeight={900}>Conversions</Typography><Typography>{getTip('conversion')}</Typography></div>}>
                    <span>Conversions</span>
                </Tooltip>
            ),
            accessorKey: 'CONVERSIONS',
            Cell: ({ row }) => <NumericFormat decimalScale={0} thousandSeparator="," value={getAwarenessMetrics(row.original).conversions} displayType="text" />,
            Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={metricTotals.conversions} displayType="text" />
        },
        {
            header: (
                <Tooltip title={<div><Typography fontWeight={900}>Conversion Rate</Typography><Typography>{getTip('conv_rate')}</Typography></div>}>
                    <span>Conv %</span>
                </Tooltip>
            ),
            accessorKey: 'CONV_RATE',
            Cell: ({ row }) => <NumericFormat decimalScale={2} value={getAwarenessMetrics(row.original).convRate} displayType="text" suffix="%" />,
            Footer: () => <NumericFormat decimalScale={2} value={metricTotals.convRate} displayType="text" suffix="%" />
        }
    ]), [getTip, metricTotals]);

    const columns = useMemo(() => {
        const baseColumns = props.appendDefaultMetrics === false
            ? [...(props.columns || [])]
            : [...(props.columns || []), ...awarenessColumns];
        const primaryColumnId = baseColumns[0]?.accessorKey || baseColumns[0]?.id || null;
        const getColumnId = (col) => col.accessorKey || col.id || col.header || col.title;

        return baseColumns.map((col) => {
            const columnId = getColumnId(col);
            const isPrimary = primaryColumnId && columnId === primaryColumnId;

            return {
                ...col,
                header: col.header || col.title,
                muiTableHeadCellProps: {
                    sx: {
                        background: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        fontSize: fontSize,
                        fontWeight: 800,
                        padding: theme.spacing(0.75),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        textAlign: isPrimary ? 'left' : 'right',
                        whiteSpace: isPrimary ? 'normal' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }
                },
                muiTableBodyCellProps: ({ column }) => {
                    const isPrimaryCol = primaryColumnId && (column.id === primaryColumnId || column.accessorKey === primaryColumnId);
                    return {
                        'data-report-metric': getReportMetricKind(column.id) || getReportMetricKind(col.header),
                        sx: {
                            background: 'transparent',
                            fontWeight: isPrimaryCol ? 700 : 600,
                            textAlign: isPrimaryCol ? 'left' : 'right',
                            fontSize: fontSize,
                            padding: theme.spacing(compact ? 0.5 : 0.75),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            whiteSpace: isPrimaryCol ? 'normal' : 'nowrap',
                            wordBreak: isPrimaryCol ? 'break-word' : 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    };
                },
                muiTableFooterCellProps: {
                    sx: {
                        textAlign: 'right',
                        padding: theme.spacing(0.75),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        fontSize: fontSize,
                        fontWeight: 900,
                        ...getReportTotalColors(theme)
                    }
                }
            };
        });
    }, [awarenessColumns, compact, props.columns, props.appendDefaultMetrics, theme]);

    const getColumnLabel = (col) => {
        const raw = typeof col.header === 'string'
            ? col.header
            : (typeof col.Header === 'string' ? col.Header : (typeof col.title === 'string' ? col.title : (col.accessorKey || col.id || '')));
        if (!raw) return 'Value';
        const normalized = String(raw).replace(/_/g, ' ').trim();
        if (/^[A-Z0-9\s]+$/.test(normalized)) {
            const compactLabel = normalized.replace(/\s+/g, '');
            if (compactLabel.length <= 4) return normalized;
            return normalized.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
        }
        return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const isMetricLikeColumn = (col) => {
        const raw = `${col?.accessorKey || ''} ${col?.id || ''} ${getColumnLabel(col)}`.toLowerCase().replace(/[^a-z0-9]+/g, '');
        const metricTokens = [
            'impressions',
            'clicks',
            'ctr',
            'conversions',
            'conv',
            'rate',
            'reach',
            'frequency',
            'views',
            'viewthrough',
            'engagement',
            'completedplays',
            'completion',
            'cost',
            'spend',
            'revenue',
            'sales',
            'roas',
            'roi',
            'cpc',
            'cpm',
            'cpv',
            'cpa',
            'cpl',
            'cpp'
        ];

        return metricTokens.some((token) => raw.includes(token));
    };

    const getCellValue = (row, col) => {
        if (typeof col.accessorFn === 'function') return col.accessorFn(row);
        if (col.accessorKey && row && Object.prototype.hasOwnProperty.call(row, col.accessorKey)) {
            return row[col.accessorKey];
        }
        if (col.id && row && Object.prototype.hasOwnProperty.call(row, col.id)) {
            return row[col.id];
        }
        return null;
    };

    const renderCellValue = (row, col) => {
        const cellValue = getCellValue(row, col);
        if (typeof col.Cell === 'function') {
            const rendered = col.Cell({
                cell: { getValue: () => cellValue, row: { original: row } },
                row: { original: row },
                column: { id: col.accessorKey || col.id }
            });
            return rendered;
        }
        if (cellValue == null || cellValue === '') return '—';
        return String(cellValue);
    };

    const defaultColumn = useMemo(() => ({
        minSize: 80,
        size: 120,
        maxSize: 320
    }), []);

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minWidth: 0, width: '100%' }}>
                {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                        {dataArray.map((row, index) => {
                            const primaryCol = columns[0];
                            const primaryValue = primaryCol ? renderCellValue(row, primaryCol) : `Row ${index + 1}`;
                            const detailColumns = columns.slice(1);
                            const supportingColumns = detailColumns.filter((col) => !isMetricLikeColumn(col));
                            const metricColumns = detailColumns.filter((col) => isMetricLikeColumn(col));

                            return (
                                <Paper key={`vimarc-mrt-card-${instanceKey}-${index}`} variant="outlined" sx={{ ...surfacePaperSx, p: 1.1 }}>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 800,
                                                lineHeight: 1.25,
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {primaryValue || `Row ${index + 1}`}
                                        </Typography>

                                        {supportingColumns.length > 0 ? (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: supportingColumns.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                                                    gap: 0.85
                                                }}
                                            >
                                                {supportingColumns.map((col, colIndex) => (
                                                    <Box data-report-metric={getReportMetricKind(col.accessorKey || col.id) || getReportMetricKind(col.header)} key={`vimarc-mrt-card-support-${instanceKey}-${index}-${colIndex}`} sx={{ display: 'grid', gap: 0.15 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.06em',
                                                                lineHeight: 1.1
                                                            }}
                                                        >
                                                            {getColumnLabel(col)}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                fontSize: '0.95rem',
                                                                fontWeight: 700,
                                                                lineHeight: 1.25,
                                                                wordBreak: 'break-word',
                                                                '& .MuiTypography-root': {
                                                                    lineHeight: 1.25
                                                                }
                                                            }}
                                                        >
                                                            {renderCellValue(row, col)}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : null}

                                        {metricColumns.length > 0 ? (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                                    gap: 0.75
                                                }}
                                            >
                                                {metricColumns.map((col, colIndex) => (
                                                    <Box
                                                        data-report-metric={getReportMetricKind(col.accessorKey || col.id) || getReportMetricKind(col.header)} key={`vimarc-mrt-card-metric-${instanceKey}-${index}-${colIndex}`}
                                                        sx={{
                                                            display: 'grid',
                                                            gap: 0.18,
                                                            p: 0.8,
                                                            borderRadius: 2,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            background: theme.palette.background.default,
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.05em',
                                                                lineHeight: 1.1
                                                            }}
                                                        >
                                                            {getColumnLabel(col)}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                fontSize: '0.98rem',
                                                                fontWeight: 800,
                                                                lineHeight: 1.2,
                                                                overflowWrap: 'anywhere'
                                                            }}
                                                        >
                                                            {renderCellValue(row, col)}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                    <MaterialReactTable
                        key={`vimarc-mrt-${instanceKey}-${String(!!props.active)}`}
                        data={dataArray}
                        getRowId={props.getRowId || ((row, i) => row.ID ?? row.id ?? row.CAMPAIGN_ID ?? `${instanceKey}-${i}`)}
                        defaultColumn={defaultColumn}
                        layoutMode="semantic"
                        enableRowVirtualization={false}
                        muiTableContainerProps={{
                            sx: {
                                overflowX: 'auto',
                                width: '100%',
                                maxWidth: '100%',
                                display: 'block',
                                willChange: 'transform',
                                ...(compact ? { maxHeight: '70vh' } : {})
                            }
                        }}
                        muiTableProps={{
                            sx: {
                                width: '100%',
                                tableLayout: 'auto',
                                minWidth: 0
                            }
                        }}
                        columns={columns}
                        enableTopToolbar={false}
                        enableFullScreenToggle={false}
                        enableColumnResizing={false}
                        enableFilters={false}
                        enableGlobalFilter={false}
                        enableDensityToggle={false}
                        enableGrouping={false}
                        enableHiding={false}
                        enableRowSelection={false}
                        enableColumnOrdering={false}
                        enableBottomToolbar={dataArray.length > 10}
                        enablePagination={dataArray.length > 10}
                        state={{}}
                        initialState={compact ? { density: 'compact' } : {}}
                        muiTableHeadCellProps={{
                            sx: {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                ...(compact ? { px: 1, py: 0.75, fontSize } : {})
                            }
                        }}
                        muiTableBodyCellProps={({ column }) => ({
                            'data-report-metric': getReportMetricKind(column.id) || getReportMetricKind(column.columnDef.header),
                            sx: {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                ...(compact ? { px: 1, py: 0.75, fontSize } : {})
                            }
                        })}
                        muiTableFooterCellProps={{
                            align: 'right',
                            sx: {
                                textAlign: 'center',
                                p: 0.5,
                                m: 0,
                                alignItems: 'center'
                            }
                        }}
                        muiTablePaperProps={{
                            elevation: 0,
                            sx: {
                                ...surfacePaperSx,
                                ...getReportTableSx(theme),
                                width: '100%',
                                maxWidth: '100%'
                            }
                        }}
                        muiTableBodyRowProps={({ row }) => ({
                            sx: {
                                textAlign: 'center',
                                backgroundColor: row.index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.default,
                                '&:hover td': {
                                    backgroundColor: `${theme.palette.action.hover} !important`
                                }
                            }
                        })}
                        renderDetailPanel={props.renderDetailPanel}
                        enableExpandAll={false}
                    />
                )}
            </Box>
        </ThemeProvider>
    );
};

export default ViMarcTableCard;
